import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-timezone';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useModal } from '../../hooks/useModal';
import useTranslation from '../../hooks/useTranslation';
import { useAppointment } from '../../hooks/useAppointment';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import {
  AppointmentBooking,
  AvailableTimeWindow,
  TimeSlot,
} from '../../types/appointment';
import Spinner from '../Spinner';
import DatePickerComponent from '../DatePicker';
import TimezoneSelector from '../TimezoneSelector';
import TimeInput from '../TimeInput';
import UserInfoForm from '../UserInfoForm';

const APPOINTMENT_DURATION_MINUTES = 45;

const AppointmentModal: React.FC = () => {
  const { t, language } = useTranslation();
  const { isOpen, interestedPlan, closeAppointmentModal } = useModal();
  const {
    loading,
    error,
    fetchAvailableTimeWindows,
    bookAppointment,
    clearError,
  } = useAppointment();

  const [activeStep, setActiveStep] = useState(0);
  const [availableWindows, setAvailableWindows] = useState<
    AvailableTimeWindow[]
  >([]);
  const [success, setSuccess] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const {
    showSpinner,
    start: startSpinner,
    stop: stopSpinner,
    reset: resetSpinner,
  } = useDeferredLoading();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string>('');

  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    company: '',
  });
  const [notes, setNotes] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const wasSuccessful = useRef(false);

  const steps = [
    t('contact.booking.steps.selectDateTime'),
    t('contact.booking.steps.yourInformation'),
    t('contact.booking.steps.confirmBooking'),
  ];

  const fetchAvailableSlots = async () => {
    setContentReady(false);
    resetSpinner();
    startSpinner();
    try {
      const slots = await fetchAvailableTimeWindows();
      setAvailableWindows(slots);
    } catch (err) {
      // Error is handled by the hook and displayed in the UI
    } finally {
      setContentReady(true);
      stopSpinner();
    }
  };

  useLayoutEffect(() => {
    if (isOpen && availableWindows.length === 0) {
      setContentReady(false);
      startSpinner();
    }
  }, [isOpen, availableWindows.length, startSpinner]);

  useEffect(() => {
    if (isOpen) {
      if (interestedPlan) setSelectedPlan(interestedPlan);
      if (availableWindows.length === 0) fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    closeAppointmentModal();
  };

  const handleExited = () => {
    if (wasSuccessful.current) {
      setActiveStep(0);
      setSelectedDate(null);
      setSelectedTimeSlot(null);
      setSelectedTimezone('');
      setUserInfo({ name: '', email: '', company: '' });
      setNotes('');
      setSelectedPlan('');
      setAvailableWindows([]);
      setContentReady(false);
      wasSuccessful.current = false;
    }
    setSuccess(false);
    resetSpinner();
    clearError();
  };

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedTimeSlot) return;

    try {
      const bookingData: AppointmentBooking = {
        selectedDate,
        selectedTimeSlot,
        timezone: selectedTimezone,
        duration: APPOINTMENT_DURATION_MINUTES,
        languageCode: language,
        userInfo,
        notes: notes.trim() || undefined,
        interestedPlan: selectedPlan || undefined,
      };

      await bookAppointment(bookingData);
      wasSuccessful.current = true;
      setSuccess(true);
      setActiveStep(3);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return (
          selectedDate !== null &&
          selectedTimeSlot !== null &&
          selectedTimezone !== ''
        );
      case 1:
        return userInfo.name.trim() !== '' && userInfo.email.trim() !== '';
      default:
        return true;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TimezoneSelector
              selectedTimezone={selectedTimezone}
              onTimezoneChange={setSelectedTimezone}
            />

            <DatePickerComponent
              availableWindows={availableWindows}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              timezone={selectedTimezone}
            />

            <TimeInput
              availableWindows={availableWindows}
              selectedDate={selectedDate}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={setSelectedTimeSlot}
              timezone={selectedTimezone}
              duration={APPOINTMENT_DURATION_MINUTES}
            />
          </Box>
        );

      case 1:
        return (
          <UserInfoForm userInfo={userInfo} onUserInfoChange={setUserInfo} />
        );

      case 2: {
        const summaryRows = [
          {
            label: t('contact.booking.confirmation.date'),
            value: selectedDate
              ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString(
                  language,
                  {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }
                )
              : '',
          },
          {
            label: t('contact.booking.confirmation.time'),
            value: selectedTimeSlot
              ? moment(selectedTimeSlot.startTime)
                  .tz(selectedTimezone)
                  .format('LT')
              : '',
          },
          {
            label: t('contact.booking.confirmation.duration'),
            value: `${APPOINTMENT_DURATION_MINUTES} ${t('contact.booking.minutes')}`,
          },
          {
            label: t('contact.booking.confirmation.timezone'),
            value: selectedTimezone,
          },
          {
            label: t('contact.booking.confirmation.name'),
            value: userInfo.name,
          },
          {
            label: t('contact.booking.confirmation.email'),
            value: userInfo.email,
          },
          ...(userInfo.company.trim()
            ? [
                {
                  label: t('contact.booking.confirmation.company'),
                  value: userInfo.company,
                },
              ]
            : []),
        ];

        return (
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('contact.booking.confirmation.title')}
            </Typography>
            <Box
              sx={{
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              {summaryRows.map(({ label, value }) => (
                <Box
                  key={label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '140px 1fr',
                    px: 2.5,
                    py: 1.5,
                    '&:not(:last-child)': {
                      borderBottom: 1,
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel shrink>
                {t('contact.booking.confirmation.interestedPlan')}
              </InputLabel>
              <Select
                value={selectedPlan}
                label={t('contact.booking.confirmation.interestedPlan')}
                onChange={e => setSelectedPlan(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  {t('contact.booking.confirmation.interestedPlanDefault')}
                </MenuItem>
                <MenuItem value={t('plans.standard.name')}>
                  {t('plans.standard.name')}
                </MenuItem>
                <MenuItem value={t('plans.secure.name')}>
                  {t('plans.secure.name')}
                </MenuItem>
                <MenuItem value={t('plans.enterprise.name')}>
                  {t('plans.enterprise.name')}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              autoFocus
              rows={4}
              label={t('contact.booking.confirmation.notes')}
              placeholder={t('contact.booking.confirmation.notesPlaceholder')}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              sx={{ mt: 3 }}
            />
          </Box>
        );
      }

      case 3:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              py: 4,
              px: 1,
            }}
          >
            <Typography
              variant="h5"
              component="p"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              {t('contact.booking.success.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('contact.booking.success.message')}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={success ? 'xs' : 'md'}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            p: 1,
          },
        },
        transition: { onExited: handleExited },
      }}
    >
      <DialogTitle sx={{ pb: success ? 0 : 1 }}>
        <Box
          display="flex"
          justifyContent={success ? 'flex-end' : 'space-between'}
          alignItems="center"
        >
          {!success && (
            <Typography variant="h5" component="h2" fontWeight={600}>
              {t('contact.booking.title')}
            </Typography>
          )}
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ ml: success ? 0 : 2 }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: success ? 1 : 2 }}>
        {!success && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('contact.booking.subtitle')}
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={fetchAvailableSlots}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Box sx={{ position: 'relative', minHeight: success ? 0 : 280 }}>
          <Fade
            in={showSpinner}
            unmountOnExit
            timeout={{ enter: 200, exit: 150 }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 112 }}>
                <Spinner message={t('contact.booking.loading')} />
              </Box>
            </Box>
          </Fade>

          {/* Content: always in DOM to anchor the container height */}
          <Fade
            in={contentReady && !showSpinner}
            timeout={{ enter: 250, exit: 200 }}
          >
            <Box>
              {!error && !success && availableWindows.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    py: 4,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {t('contact.booking.noSlotsAvailable')}
                  </Typography>
                </Box>
              ) : (
                renderStepContent(activeStep)
              )}
            </Box>
          </Fade>
        </Box>
      </DialogContent>

      {!success ? (
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ minWidth: 100 }}>
              {t('contact.booking.buttons.back')}
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
              sx={{ minWidth: 100 }}
            >
              {t('contact.booking.buttons.next')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleBookAppointment}
              disabled={!isStepValid(activeStep) || loading}
              sx={{ minWidth: 160 }}
              loading={loading}
            >
              {t('contact.booking.buttons.confirm')}
            </Button>
          )}
        </DialogActions>
      ) : (
        <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleClose}
            fullWidth
            sx={{ maxWidth: 280 }}
          >
            {t('contact.booking.buttons.done')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AppointmentModal;
